export const REMINDER_SOUND_OPTIONS = [
  { value: 'gentle-chime', label: 'Gentle Chime' },
  { value: 'digital-alarm', label: 'Digital Alarm' },
  { value: 'classic-bell', label: 'Classic Bell' },
  { value: 'soft-pulse', label: 'Soft Pulse' },
  { value: 'urgent-beep', label: 'Urgent Beep' },
  { value: 'double-ding', label: 'Double Ding' },
  { value: 'ascending-tone', label: 'Ascending Tone' },
  { value: 'none', label: 'No Sound' },
];

export const REMINDER_SOUND_CYCLE_PAUSE_MS = 1500;
export const REMINDER_SOUND_SAFETY_MAX_MS = 10 * 60 * 1000;

const PATTERNS = {
  'gentle-chime': [
    { at: 0, frequency: 659, duration: 0.22, gain: 0.45, type: 'sine' },
    { at: 0.38, frequency: 880, duration: 0.28, gain: 0.3, type: 'sine' },
  ],
  'digital-alarm': [
    { at: 0, frequency: 880, duration: 0.14, gain: 0.5, type: 'square' },
    { at: 0.3, frequency: 880, duration: 0.14, gain: 0.5, type: 'square' },
    { at: 0.6, frequency: 880, duration: 0.14, gain: 0.5, type: 'square' },
  ],
  'classic-bell': [
    { at: 0, frequency: 523, duration: 0.25, gain: 0.42, type: 'sine' },
    { at: 0.42, frequency: 1046, duration: 0.24, gain: 0.25, type: 'sine' },
  ],
  'soft-pulse': [
    { at: 0, frequency: 440, duration: 0.18, gain: 0.25, type: 'sine' },
    { at: 0.36, frequency: 440, duration: 0.18, gain: 0.22, type: 'sine' },
  ],
  'urgent-beep': [
    { at: 0, frequency: 1100, duration: 0.12, gain: 0.55, type: 'sawtooth' },
    { at: 0.26, frequency: 1100, duration: 0.12, gain: 0.55, type: 'sawtooth' },
    { at: 0.52, frequency: 1100, duration: 0.12, gain: 0.55, type: 'sawtooth' },
  ],
  'double-ding': [
    { at: 0, frequency: 784, duration: 0.2, gain: 0.4, type: 'sine' },
    { at: 0.38, frequency: 988, duration: 0.25, gain: 0.4, type: 'sine' },
  ],
  'ascending-tone': [
    { at: 0, frequency: 440, duration: 0.16, gain: 0.35, type: 'sine' },
    { at: 0.31, frequency: 554, duration: 0.16, gain: 0.35, type: 'sine' },
    { at: 0.62, frequency: 659, duration: 0.22, gain: 0.38, type: 'sine' },
  ],
};

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  return AudioContext ? new AudioContext() : null;
}

export function getReminderSoundPatternDuration(soundType = 'gentle-chime') {
  const pattern = PATTERNS[soundType] || [];
  return Math.ceil(Math.max(0, ...pattern.map((tone) => tone.at + tone.duration)) * 1000);
}

export async function playReminderPattern(soundType = 'gentle-chime', volume = 65) {
  if (soundType === 'none') return { played: false, reason: 'disabled' };
  const pattern = PATTERNS[soundType];
  if (!pattern) return { played: false, reason: 'unsupported-sound' };

  const context = getAudioContext();
  if (!context) return { played: false, reason: 'unsupported' };

  try {
    if (context.state === 'suspended') await context.resume();
    const masterGain = context.createGain();
    masterGain.gain.value = Math.max(0, Math.min(100, Number(volume) || 0)) / 100;
    masterGain.connect(context.destination);
    const now = context.currentTime;
    const oscillators = new Set();
    const gains = new Set([masterGain]);
    const durationMs = getReminderSoundPatternDuration(soundType);
    let finishTimer;
    let finished = false;
    let resolveFinished;
    const finishedPromise = new Promise((resolve) => { resolveFinished = resolve; });

    const stop = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(finishTimer);
      oscillators.forEach((oscillator) => {
        try { oscillator.stop(); } catch { /* oscillator already ended */ }
        try { oscillator.disconnect(); } catch { /* already disconnected */ }
      });
      gains.forEach((gain) => {
        try { gain.disconnect(); } catch { /* already disconnected */ }
      });
      context.close().catch(() => {});
      resolveFinished();
    };

    pattern.forEach((tone) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillators.add(oscillator);
      gains.add(gain);
      const start = now + tone.at;
      const end = start + tone.duration;
      oscillator.type = tone.type;
      oscillator.frequency.setValueAtTime(tone.frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.001, tone.gain), start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      oscillator.connect(gain);
      gain.connect(masterGain);
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    });

    finishTimer = window.setTimeout(stop, durationMs + 40);
    return {
      played: true,
      durationMs,
      stop,
      finished: finishedPromise,
    };
  } catch {
    context.close().catch(() => {});
    return { played: false, reason: 'blocked' };
  }
}

export async function playReminderSound(soundType = 'gentle-chime', volume = 65) {
  return playReminderPattern(soundType, volume);
}

export class ReminderSoundLoopController {
  constructor({
    playPattern = playReminderPattern,
    now = () => Date.now(),
    setTimer = (callback, delay) => window.setTimeout(callback, delay),
    clearTimer = (timer) => window.clearTimeout(timer),
    cyclePauseMs = REMINDER_SOUND_CYCLE_PAUSE_MS,
    safetyMaxMs = REMINDER_SOUND_SAFETY_MAX_MS,
  } = {}) {
    this.playPattern = playPattern;
    this.now = now;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;
    this.cyclePauseMs = cyclePauseMs;
    this.safetyMaxMs = safetyMaxMs;
    this.timeouts = new Set();
    this.activePattern = null;
    this.activeTaskId = null;
    this.generation = 0;
    this.cycleActive = false;
  }

  start(reminder, { taskId = null } = {}) {
    this.stop();
    if (!reminder?.sound || reminder.soundType === 'none') return false;

    this.generation += 1;
    const generation = this.generation;
    const startedAt = this.now();
    const repeatMode = reminder.soundRepeatMode || 'once';
    const deadline = repeatMode === 'duration'
      ? startedAt + (Number(reminder.soundDurationSeconds) * 1000)
      : repeatMode === 'until_dismissed'
        ? startedAt + this.safetyMaxMs
        : null;
    this.activeTaskId = taskId;

    const finishLoop = () => {
      if (generation !== this.generation) return;
      this.activeTaskId = null;
      this.cycleActive = false;
      this.activePattern = null;
    };

    const runCycle = async () => {
      if (generation !== this.generation) return;
      if (deadline != null && this.now() > deadline) {
        finishLoop();
        return;
      }

      this.cycleActive = true;
      const pattern = await this.playPattern(reminder.soundType, reminder.soundVolume);
      if (generation !== this.generation) {
        pattern?.stop?.();
        return;
      }
      if (!pattern?.played) {
        finishLoop();
        return;
      }
      this.activePattern = pattern;
      await pattern.finished;
      if (generation !== this.generation) return;
      this.activePattern = null;
      this.cycleActive = false;

      if (repeatMode === 'once' || (deadline != null && this.now() >= deadline)) {
        finishLoop();
        return;
      }

      const timeout = this.setTimer(() => {
        this.timeouts.delete(timeout);
        if (generation !== this.generation) return;
        if (deadline != null && this.now() > deadline) {
          finishLoop();
          return;
        }
        runCycle();
      }, this.cyclePauseMs);
      this.timeouts.add(timeout);
    };

    runCycle();
    return true;
  }

  stop() {
    this.generation += 1;
    this.timeouts.forEach((timeout) => this.clearTimer(timeout));
    this.timeouts.clear();
    this.activePattern?.stop?.();
    this.activePattern = null;
    this.activeTaskId = null;
    this.cycleActive = false;
  }

  destroy() {
    this.stop();
  }
}
